import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

import CustomModal from "../../packages/CustomModal";
import FileUploadCard from "../FileUploadCard/FileUploadCard";
import FileDetailsCard from "../FileUploadCard/FileDetailsCard";
import InputBox from "../InputBox";
import {
  getCIPs,
  getadvanceSettings,
  uploadBulkCIPs,
} from "../../network/cipAPI";
import {
  CustomModalButtons,
  FILE_UPLOAD,
  FILE_UPLOAD_STATUS,
} from "../../constants/global.constants";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../Notifications";

import "./BulkUploadDialog.scss";

const BulkUploadDialog = ({
  open,
  onClose,
  onUploaded,
  setView,
  selectedTrain,
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProceed, setIsProceed] = useState(false);
  const [status, setStatus] = useState(FILE_UPLOAD_STATUS.IDLE);
  const [progress, setProgress] = useState(0);
  const [isAdvanceSettingsLoading, setIsAdvanceSettingsLoading] =
    useState(false);
  const [durationPerBw, setDurationPerBw] = useState("");
  const [durationBetweenBw, setDurationBetweenBw] = useState("");
  const [durationPerBwError, setDurationPerBwError] = useState("");
  const [durationBetweenBwError, setDurationBetweenBwError] = useState("");

  useEffect(() => {
    if (!open) return;

    const formatToTwoDecimals = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
    };

    const fetchAdvanceSettings = async () => {
      setIsAdvanceSettingsLoading(true);
      try {
        const response = await getadvanceSettings();
        const data = response?.data || {};

        const time1 = Number(data.time1);
        const shortTimeThreshold = Number(data.short_time_threshold);

        const perBw = Number.isFinite(time1)
          ? time1
          : (data.duration_per_bw ?? data.design_bw_duration ?? "");
        const betweenBw = Number.isFinite(shortTimeThreshold)
          ? (shortTimeThreshold * 10) / 60
          : (data.duration_between_bw ?? data.design_duration_btw_bw ?? "");

        setDurationPerBw(perBw === null ? "" : formatToTwoDecimals(perBw));
        setDurationBetweenBw(
          betweenBw === null ? "" : formatToTwoDecimals(betweenBw),
        );
      } catch (e) {
        setDurationPerBw("");
        setDurationBetweenBw("");
      } finally {
        setIsAdvanceSettingsLoading(false);
      }
    };

    fetchAdvanceSettings();
  }, [open]);

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const validateFile = async (file) => {
    if (!file) {
      return t("CIPShutdownStrings.selectBulkFileError");
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isValidType = FILE_UPLOAD.SUPPORTED_FILE_TYPES.some(
      (type) => type.toLowerCase() === fileExtension,
    );

    if (!isValidType) {
      await wait(300);
      return t("fileUpload.invalidFileType");
    }

    const maxSizeInBytes =
      parseInt(FILE_UPLOAD.MAX_FILE_SIZE, 10) * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      setProgress(90);
      await wait(300);
      return t("fileUpload.fileTooLarge", { size: FILE_UPLOAD.MAX_FILE_SIZE });
    }

    try {
      setProgress(50);
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: "array" });

      const allSheetsEmpty = workbook.SheetNames.every((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        return (
          data.length === 0 ||
          data.every(
            (row) =>
              !Array.isArray(row) ||
              row.every(
                (cell) =>
                  cell === null ||
                  cell === undefined ||
                  `${cell}`.trim() === "",
              ),
          )
        );
      });

      if (allSheetsEmpty) {
        await wait(300);
        return t("fileUpload.noData");
      }

      setProgress(100);
      await wait(250);
    } catch (e) {
      await wait(300);
      return t("fileUpload.invalidFileType");
    }

    return "";
  };

  const onFileSelected = async (file) => {
    setError("");
    setStatus(FILE_UPLOAD_STATUS.IDLE);
    setProgress(0);
    setSelectedFile(file);
  };

  const handleDialogClose = () => {
    onClose?.();
  };

  const handleModalClose = (action) => {
    const actionId = action?.target?.id || action;
    if (
      actionId === CustomModalButtons.CANCEL ||
      actionId === CustomModalButtons.CLOSE_BTN
    ) {
      handleDialogClose();
    }
  };

  const handleUpload = async () => {
    setDurationPerBwError("");
    setDurationBetweenBwError("");

    let hasDurationError = false;
    if (
      durationPerBw === "" ||
      durationPerBw === null ||
      durationPerBw === undefined
    ) {
      setDurationPerBwError(t("CIPShutdownStrings.bulkDurationPerBwRequired"));
      hasDurationError = true;
    }
    if (
      durationBetweenBw === "" ||
      durationBetweenBw === null ||
      durationBetweenBw === undefined
    ) {
      setDurationBetweenBwError(
        t("CIPShutdownStrings.bulkDurationBetweenBwRequired"),
      );
      hasDurationError = true;
    }

    if (hasDurationError) {
      setStatus(FILE_UPLOAD_STATUS.ERROR);
      return;
    }

    setStatus(FILE_UPLOAD_STATUS.LOADING);
    setProgress(20);
    const validationError = await validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      setStatus(FILE_UPLOAD_STATUS.ERROR);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (durationPerBw !== "") {
        formData.append("duration_per_bw", durationPerBw);
      }
      if (durationBetweenBw !== "") {
        formData.append("duration_between_bw", durationBetweenBw);
      }

      const parsedTrain = Number(selectedTrain);
      const resolvedTrain =
        Number.isFinite(parsedTrain) && parsedTrain > 0 ? parsedTrain : 1;

      const response = await uploadBulkCIPs(resolvedTrain, formData);
      const uploadResult = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : null;
      const isSuccess = !!uploadResult || response?.status === 200;

      if (isSuccess) {
        let allCips = uploadResult || [];

        // Fallback for APIs that return only status/message instead of CIP list.
        if (!uploadResult) {
          const cips = await getCIPs();
          allCips = Array.isArray(cips) ? cips : cips?.data || [];
        }

        const filteredCips = allCips.filter((item) => !item.is_tool_identified);

        onUploaded?.(filteredCips, allCips);
        setView?.(2);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("notificationStrings.fileSuccess"),
          "",
        );
        setStatus(FILE_UPLOAD_STATUS.SUCCESS);
        handleDialogClose();
      } else {
        setError(
          response?.detail?.error || t("notificationStrings.somethingWrong"),
        );
        setStatus(FILE_UPLOAD_STATUS.ERROR);
      }
    } catch (err) {
      setError(err?.message || t("notificationStrings.somethingWrong"));
      setStatus(FILE_UPLOAD_STATUS.ERROR);
      createNotification(
        ERROR_NOTIFICATION,
        err?.message || t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <CustomModal
      header={t("CIPShutdownStrings.addBulk")}
      cancelBtn={t("ValueStrings.cancel")}
      onModalClose={handleModalClose}
      isLoading={isAdvanceSettingsLoading}
      customclass="bulk-upload-dialog"
      width="850px"
    >
      <div className="bulk-upload-dialog-body">
        <div className="bulk-date-fields-row">
          <div className="bulk-date-field">
            <InputBox
              id="bulk-duration-per-bw"
              type="number"
              labelText={t("CIPShutdownStrings.bulkDurationOfBackwash")}
              value={durationPerBw}
              onChange={(value) => {
                setDurationPerBw(value);
                setDurationPerBwError("");
              }}
              errorMsg={durationPerBwError}
              isRequired
              customClass="bulk-duration-input-box"
              hideClearBtn
            />
          </div>

          <div className="bulk-date-field">
            <InputBox
              id="bulk-duration-between-bw"
              type="number"
              labelText={t("CIPShutdownStrings.bulkDurationBetweenBackwashes")}
              value={durationBetweenBw}
              onChange={(value) => {
                setDurationBetweenBw(value);
                setDurationBetweenBwError("");
              }}
              errorMsg={durationBetweenBwError}
              isRequired
              customClass="bulk-duration-input-box"
              hideClearBtn
            />
          </div>
        </div>

        <div
          className={`bulk-upload-card-container ${
            selectedFile
              ? "bulk-upload-card-container--selected"
              : "bulk-upload-card-container--empty"
          }`}
        >
          {selectedFile ? (
            <FileDetailsCard
              uploadedFile={selectedFile}
              setUploadedFile={onFileSelected}
              status={status}
              progress={progress}
              error={error}
              isLoading={isLoading}
              onUploadClick={handleUpload}
              showDownload
            />
          ) : (
            <FileUploadCard
              id="bulk-upload-card"
              hoverable
              uploadedFile={selectedFile}
              setUploadedFile={onFileSelected}
              isProceed={isProceed}
              setIsProceed={setIsProceed}
              showDownload
              size="180px"
            />
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default BulkUploadDialog;
